import { filter, get, groupBy, partition } from 'lodash'
import { createSelector } from 'reselect'

import { RootState } from '@/store'

import {
  complexAggregateAttributeToSubAttributeMap,
  individualNonComplexPredicate,
  targetingRuleSetsPredicate
} from '.'

type OrganizationUserAttributesPageState = {
  byId: Record<string, any>
}

const orgUserAttrById: OrganizationUserAttributesPageState = { byId: {} }
export const getAttributes = createSelector(
  (state: RootState) => get(state, 'organizationUserAttributes', orgUserAttrById).byId,
  byId => {
    return Object.keys(byId).map(id => {
      const { scimAttributeName, scimParentAttributeName, scimSchema } = byId[id]
      const prefix = scimParentAttributeName ? `${scimSchema}:${scimParentAttributeName}.` : `${scimSchema}:`

      return { ...byId[id], urn: `${prefix}${scimAttributeName}` }
    })
  }
)

export const getTargetingAttributes = createSelector(getAttributes, (byId: any) => {
  return Object.keys(byId).reduce((attributes, id) => {
    const { scimAttributeName, scimParentAttributeName, targetable, type, urn } = byId[id]
    const concatWithPredicate = (predicate: any, overrides?: any) => {
      return attributes.concat(filter(byId, predicate).map((attribute: any) => ({ ...attribute, ...overrides })))
    }

    /*
     * Legacy configurations let a sub-attribute itself be targetable which is no longer supported. Certain required
     * attributes are also always targetable regardless of an organization's configuration.
     */
    if (!['externalId', 'id', 'userName'].includes(scimAttributeName) && (!targetable || scimParentAttributeName)) {
      return attributes
    }

    // only complex attributes are targetable but we only want specific sub-attributes to be used for aggregation
    if (complexAggregateAttributeToSubAttributeMap[urn]) {
      return concatWithPredicate(
        { urn: `${urn}.${complexAggregateAttributeToSubAttributeMap[urn]}` },
        { description: byId[id].description }
      )
    }

    // for any remaining complex attributes, use all of it's sub-attributes for targeting (e.g. `name`)
    return type === 'complex'
      ? concatWithPredicate({ scimParentAttributeName: scimAttributeName })
      : attributes.concat({ ...byId[id] })
  }, [] as any[])
})

// selects all attributes that use the aggregation service to suggest possible attribute term results (not singular users)
export const getTargetingAggregateAttributes = createSelector(getTargetingAttributes, attributes =>
  attributes.filter(attribute => !targetingRuleSetsPredicate(attribute))
)

// groups attributes into rule sets that use the search service to identify singular users
export const getTargetingRuleSetAttributes = createSelector(
  [getAttributes, getTargetingAttributes],
  (attributes, targetingAttributes) => {
    const ruleSetAttributes = targetingAttributes.filter(targetingRuleSetsPredicate)
    const [complexAttributes, simpleAttributes] = partition(ruleSetAttributes, 'scimParentAttributeName')
    const ruleSetGroups = groupBy(complexAttributes, 'scimParentAttributeName')

    simpleAttributes.forEach(attribute => {
      if (individualNonComplexPredicate(attribute)) {
        ruleSetGroups.name = get(ruleSetGroups, 'name', []).concat(attribute)
      }
    })

    return ruleSetGroups
  }
)

//TODO: retire selectors once all components are using the useOrgUserAttributes hook
